import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Select} from '@wandb/weave/components/Form/Select';
import {capitalizeFirst} from '@wandb/weave/core/util/string';
import {extractErrorMessageFromApolloError} from '@wandb/weave/errors';
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useState,
} from 'react';

import {envIsLocal} from '../../config';
import {useUpdateModelsSeatMutation} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {ChangeModelSeatConfirmationModal} from './ChangeModelSeatConfirmationModal';
import {useOnUserRoleChange} from './OrgDashboardMembersTable/hooks/useOnUserRoleChange';
import {
  customSelectComponentForRoleOptions,
  RoleOption,
  TeamOrOrganizationRoleOption,
} from './TeamOrOrganizationRoleOption';
import {getModelsSeatTypeColumnName, ModelsSeatType, UserRole} from './util';

type ModelsSeatTypeDropdownProps = {
  username: string;
  userId?: string;
  organizationId?: string;
  currentModelSeat: ModelsSeatType;
  isDisabled: boolean;
  refetch?: () => void;
  roleOptions?: RoleOption[];
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  isCurrentOrgAdmin?: boolean;
  hasWeaveAccess?: boolean;
};

const ModelSeatTypeDropdownComp = ({
  username,
  isDisabled,
  roleOptions,
  currentModelSeat,
  organizationId = '',
  userId = '',
  refetch,
  setIsLoading,
  isCurrentOrgAdmin = false,
  hasWeaveAccess = true,
}: ModelsSeatTypeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  const [toModelSeat, setToModelSeat] = useState<ModelsSeatType | null>(null);

  const [updateModelsSeatMutation] = useUpdateModelsSeatMutation({
    context: propagateErrorsContext(),
  });

  const {onUserRoleChange, isUpdating} = useOnUserRoleChange();

  const onUpdateModelSeat = (newSeat: ModelsSeatType) => {
    if (
      newSeat === ModelsSeatType.VIEWER ||
      newSeat === ModelsSeatType.NOACCESS
    ) {
      setToModelSeat(newSeat);
      setShowDowngradeModal(true);
      return;
    }
    updateModelsSeatMutationCall(newSeat);
  };

  const updateModelsSeatMutationCall = useCallback(
    async (newSeat: ModelsSeatType) => {
      if (newSeat == null) {
        toast('Select appropriate model seat');
        return;
      }
      if (setIsLoading) {
        setIsLoading(true);
      }

      const seatColumnName = getModelsSeatTypeColumnName(newSeat);

      try {
        const updateModelsSeatResult = await updateModelsSeatMutation({
          variables: {
            userID: userId,
            organizationId: organizationId,
            modelsSeat: seatColumnName,
          },
        });

        if (updateModelsSeatResult.errors) {
          const errMsg = updateModelsSeatResult.errors
            .map(e => e.message)
            .join(', ');
          toast(`Error updating model seat for user: ${errMsg}`);
          if (setIsLoading) {
            setIsLoading(false);
          }
        } else if (
          updateModelsSeatResult.data &&
          updateModelsSeatResult.data.updateModelsSeat?.success
        ) {
          if (
            !hasWeaveAccess &&
            envIsLocal &&
            currentModelSeat === ModelsSeatType.VIEWER &&
            newSeat === ModelsSeatType.NOACCESS
          ) {
            // If on server, with no weave access, and changing from model seat fromv Viewer -> No Access, then updated to org role from viewer -> member
            await onUserRoleChange(userId, UserRole.Member);
          }

          toast('Successfully changed Models Seat');
          await refetch?.();
          if (setIsLoading) {
            setIsLoading(false);
          }
          setToModelSeat(null);
        }
      } catch (err) {
        const errMsg = extractErrorMessageFromApolloError(err);
        if (setIsLoading) {
          setIsLoading(false);
        }
        toast(`Error updating models seat: ${errMsg}`);
        setToModelSeat(null);
      }
    },
    [
      updateModelsSeatMutation,
      currentModelSeat,
      hasWeaveAccess,
      onUserRoleChange,
      userId,
      organizationId,
      refetch,
      setIsLoading,
    ]
  );

  if (!roleOptions) {
    return (
      <div className="flex cursor-pointer items-center pl-5 text-moon-450">
        <span>{capitalizeFirst(currentModelSeat)}</span>
      </div>
    );
  }

  return (
    <>
      <Select<RoleOption, false>
        aria-label="update user models seat"
        menuIsOpen={isOpen}
        onMenuOpen={() => setIsOpen(true)}
        onMenuClose={() => setIsOpen(false)}
        options={roleOptions}
        formatOptionLabel={option => (
          <TeamOrOrganizationRoleOption roleOption={option} />
        )}
        value={roleOptions.find(option => option.value === currentModelSeat)}
        onChange={selected => {
          if (selected?.value && !selected?.isDisabled) {
            onUpdateModelSeat(selected.value as ModelsSeatType);
          }
        }}
        components={customSelectComponentForRoleOptions()}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        isSearchable={false}
        isLoading={false}
        isDisabled={isDisabled}
      />

      {showDowngradeModal && toModelSeat && (
        <ChangeModelSeatConfirmationModal
          username={username}
          newModelSeat={toModelSeat}
          onConfirm={() => updateModelsSeatMutationCall(toModelSeat)}
          onClose={() => setShowDowngradeModal(false)}
          isCurrentOrgAdmin={isCurrentOrgAdmin}
        />
      )}
    </>
  );
};

export const ModelSeatTypeDropdown = memo(ModelSeatTypeDropdownComp);
